import dayjs from "dayjs";
import { BookingActivity, BookingActivityDisplay } from "types/audit/booking-activity";

export const transformBookingActivity = (currentRevision: BookingActivity, previousRevision?: BookingActivity): BookingActivityDisplay => {
  return {
    timestamp: currentRevision.revision.timestamp,
    action: buildActivityAction(currentRevision),
    modifiedBy: currentRevision.booking.lastUpdatedBy ?? "",
    description: buildActivityDescription(currentRevision, previousRevision),
  }
}

const buildActivityAction = (activity: BookingActivity): string => {
  if (activity.type === "ADD") {
    return "BOOKING CREATED";
  } else if (activity.type === "MOD") {
    return "EDIT BOOKING CHANGE";
  }

  // TODO: add support for EDI

  // unreachable since we don't support deleting bookings (yet)
  return "EDIT BOOKING CHANGE"
}

const buildActivityDescription = (currentRevision: BookingActivity, previousRevision?: BookingActivity): string => {
  if (currentRevision.type === "ADD") {
    const details = currentRevision.details.map(i => `${i.iso} (QTY: ${i.count})`);
    return `BOOKING CREATED WITH SIZE TYPES [${details.join(', ')}]`;
  } else if (currentRevision.updatedFields.length === 0 || !previousRevision) {
    return "NO CHANGES";
  }

  const fieldChanges = buildBookingFieldChangeDescription(currentRevision, previousRevision);
  const detailChanges = buildBookingDetailChangeDescription(currentRevision, previousRevision);

  return `${fieldChanges}${fieldChanges.length > 0 ? ' ' : ''}${detailChanges}`.toUpperCase();
}

const buildBookingFieldChangeDescription = (currentRevision: BookingActivity, previousRevision?: BookingActivity): string => {
  if (currentRevision.updatedFields.length === 0 || !previousRevision) {
    return "";
  }

  let description = "CHANGED FIELD(S) ";
  const changelog: string[] = [];

  for (const field of currentRevision.updatedFields) {
    switch (field) {
      case "bookingNumber":
        changelog.push(`BOOKING NUMBER FROM '${String(previousRevision.booking.bookingNumber)}' TO '${String(currentRevision.booking.bookingNumber)}'`);
        break;
      case "startDate":
        changelog.push(`START DATE FROM '${dayjs(previousRevision.booking.startDate).format('MM/DD/YYYY')}' TO '${dayjs(currentRevision.booking.startDate).format('MM/DD/YYYY')}'`);
        break;
      case "expirationDate":
        changelog.push(`EXPIRATION DATE FROM '${dayjs(previousRevision.booking.expirationDate).format('MM/DD/YYYY')}' TO '${dayjs(currentRevision.booking.expirationDate).format('MM/DD/YYYY')}'`);
        break;
      case "notes":
        changelog.push(`NOTES FROM '${String(previousRevision.booking.notes)}' TO '${String(currentRevision.booking.notes)}'`);
        break;
      case "bookingTemperature":
        changelog.push(`BOOKING TEMPERATURE FROM '${String(previousRevision.booking.bookingTemperature)}' TO '${String(currentRevision.booking.bookingTemperature)}'`);
        break;
      case "humidity":
        changelog.push(`HUMIDITY FROM '${String(previousRevision.booking.humidity)}' TO '${String(currentRevision.booking.humidity)}'`);
        break;
      case "ventilation":
        changelog.push(`VENTILATION FROM '${String(previousRevision.booking.ventilation)}' TO '${String(currentRevision.booking.ventilation)}'`);
        break;
      case "requiresGenset":
        changelog.push(`REQUIRES GENSET FROM '${String(previousRevision.booking.requiresGenset)}' TO '${String(currentRevision.booking.requiresGenset)}'`);
        break;
      case "vesselVoyage":
        changelog.push(`VESSEL VOYAGE FROM '${String(previousRevision.booking.vesselVoyage)}' TO '${String(currentRevision.booking.vesselVoyage)}'`);
        break;
      case "vesselLloydsNumber":
        changelog.push(`VESSEL LLOYDS NUMBER FROM '${String(previousRevision.booking.vesselLloydsNumber)}' TO '${String(currentRevision.booking.vesselLloydsNumber)}'`);
        break;
      case "vesselName":
        changelog.push(`VESSEL NAME FROM '${String(previousRevision.booking.vesselName)}' TO '${String(currentRevision.booking.vesselName)}'`);
        break;
      case "railyard":
        changelog.push(`RAILYARD FROM '${String(previousRevision.booking.railyard)}' TO '${String(currentRevision.booking.railyard)}'`);
        break;
      case "sslScac":
        changelog.push(`SSL SCAC CHANGED FROM '${String(previousRevision.booking.sslScac)}' TO '${String(currentRevision.booking.sslScac)}'`)
        break;
      default:
        break;
    }
  }

  return changelog.length > 0 ? `${description} ${changelog.join(',')}.`.toUpperCase() : "";
};

const buildBookingDetailChangeDescription = (currentRevision: BookingActivity, previousRevision?: BookingActivity): string => {
  if (!previousRevision) {
    return "";
  }

  let description = "";
  const added = currentRevision.details
    .filter(i => previousRevision.details.find(j => i.iso === j.iso) === undefined)
    .map(i => `${i.iso} (QTY: ${i.count})`);
  if (added.length > 0) {
    description = description + `ADDED SIZE TYPES [${added.join(', ')}].`;
  }

  const removed = previousRevision.details
    .filter(i => currentRevision.details.find(j => i.iso === j.iso) === undefined)
    .map(i => i.iso);
  if (removed.length > 0) {
    description = `${description}${description.length > 0 ? ' ' : ""}REMOVED SIZE TYPES [${removed.join(', ')}].`;
  }

  const changedQty = currentRevision.details
    .filter(i => previousRevision.details.find(j => i.iso === j.iso && i.count !== j.count) !== undefined)
    .map(i => `${i.iso} (QTY: ${i.count})`);
  if (changedQty.length > 0) {
    description = `${description}${description.length > 0 ? ' ' : ""}CHANGED QTY ON SIZE TYPES [${changedQty.join(', ')}].`;
  }

  return description;
};
